/**
 * Ambient runtime health — pure judgment over what `ambient_jobs` already records.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * The ambient runtime stopped ticking on 2026-07-11 and nobody noticed for
 * seven weeks. Not because the information was missing — `ambient_jobs` carries
 * `last_run_at`, `next_run_at`, `last_status` and `last_error`, and
 * `ambientRuntimeService.rowToJob` already reads all four onto the object the
 * settings page holds in state. The page simply never rendered them, and its
 * own copy said jobs run "once the scheduler tick is wired" — acknowledging the
 * tick might not be, and never checking.
 *
 * So this module answers the question nothing was asking: has this actually
 * run? It is deliberately pure (the cutReasons.ts / commitmentTerms.ts
 * precedent) with `now` injected, so both the settings page and the sidebar
 * badge reach the same verdict and neither re-derives it.
 *
 * ── The distinction that matters ───────────────────────────────────────────
 * "One job is behind" and "nothing is running at all" are different facts with
 * different causes and different fixes. A single overdue job is usually that
 * job; EVERY enabled job overdue means the external tick is not arriving, which
 * is an operator problem no amount of per-job attention will solve. The runtime
 * README is explicit that scheduling is a deploy-environment concern, so the
 * system cannot fix this itself — it can only say so clearly, which is exactly
 * what went missing.
 */

/** The subset of an AmbientJob this judgment needs. */
export interface AmbientJobHealthInput {
  jobType: string;
  enabled: boolean;
  cadenceMinutes: number;
  /** ISO timestamp the dispatcher will next claim this job. */
  nextRunAt: string | null;
  /** ISO timestamp of the last completed run; null when it has never run. */
  lastRunAt: string | null;
  lastStatus: 'completed' | 'failed' | 'skipped' | null;
  lastError: string | null;
}

export type AmbientJobState =
  /** Turned off by an admin — silence is expected, not a fault. */
  | 'disabled'
  /** Enabled, due in the future, last run within tolerance. */
  | 'healthy'
  /** Enabled and has run, but is past due by more than a full cadence. */
  | 'overdue'
  /** Enabled, past due, and has never run at all. */
  | 'never_run'
  /** Ran, and the run itself failed. */
  | 'failing';

export interface AmbientJobHealth {
  state: AmbientJobState;
  /** Whole days since the last successful claim; null when it has never run. */
  daysSinceRun: number | null;
  /** Whole minutes past `nextRunAt`; 0 when not yet due. */
  minutesLate: number;
  /** Short chip label. */
  label: string;
  /** One plain sentence. No invented specificity: when the data does not say, neither does this. */
  sentence: string;
  /** Whether this needs someone's attention now. */
  needsAttention: boolean;
}

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

/**
 * Tolerance before "late" becomes "overdue": a job may miss a full cadence
 * before we call it. A 15-minute job running 20 minutes late is scheduler
 * jitter; running 40 minutes late is not. Floored at 15 minutes so a job with
 * a tiny cadence does not alarm on ordinary slack.
 */
const MIN_GRACE_MINUTES = 15;

function graceMinutes(cadenceMinutes: number): number {
  return Math.max(MIN_GRACE_MINUTES, cadenceMinutes);
}

function minutesBetween(fromIso: string | null, nowIso: string): number | null {
  if (!fromIso) return null;
  const from = Date.parse(fromIso);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(from) || !Number.isFinite(now)) return null;
  return Math.floor((now - from) / MS_PER_MINUTE);
}

function daysBetween(fromIso: string | null, nowIso: string): number | null {
  if (!fromIso) return null;
  const from = Date.parse(fromIso);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(from) || !Number.isFinite(now)) return null;
  return Math.max(0, Math.floor((now - from) / MS_PER_DAY));
}

/** "52 days" / "3 hours" / "12 minutes" — the largest unit that stays honest. */
export function describeElapsed(minutes: number): string {
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function judgeAmbientJob(
  job: AmbientJobHealthInput,
  nowIso: string,
): AmbientJobHealth {
  const daysSinceRun = daysBetween(job.lastRunAt, nowIso);
  const lateBy = minutesBetween(job.nextRunAt, nowIso) ?? 0;
  const minutesLate = Math.max(0, lateBy);

  if (!job.enabled) {
    return {
      state: 'disabled',
      daysSinceRun,
      minutesLate,
      label: 'Off',
      sentence: 'Turned off — this job is not scheduled to run.',
      needsAttention: false,
    };
  }

  const overdue = minutesLate > graceMinutes(job.cadenceMinutes);

  if (overdue && !job.lastRunAt) {
    return {
      state: 'never_run',
      daysSinceRun: null,
      minutesLate,
      label: 'Never run',
      sentence: `Enabled and due ${describeElapsed(minutesLate)} ago, but it has never run.`,
      needsAttention: true,
    };
  }

  if (overdue) {
    return {
      state: 'overdue',
      daysSinceRun,
      minutesLate,
      label: 'Overdue',
      sentence:
        `Due ${describeElapsed(minutesLate)} ago and has not run since` +
        (daysSinceRun === null ? '.' : ` ${describeElapsed(daysSinceRun * 24 * 60)} ago.`),
      needsAttention: true,
    };
  }

  // A failing run is worth flagging even while the schedule itself is on time.
  if (job.lastStatus === 'failed') {
    return {
      state: 'failing',
      daysSinceRun,
      minutesLate,
      label: 'Last run failed',
      sentence: job.lastError?.trim()
        ? `The last run failed: ${job.lastError.trim()}`
        : 'The last run failed. No error was recorded.',
      needsAttention: true,
    };
  }

  return {
    state: 'healthy',
    daysSinceRun,
    minutesLate,
    label: 'On schedule',
    sentence: job.lastRunAt
      ? `Last ran ${describeElapsed(minutesBetween(job.lastRunAt, nowIso) ?? 0)} ago.`
      : 'Enabled and waiting for its first run.',
    needsAttention: false,
  };
}

export interface AmbientRuntimeHealth {
  /** Enabled jobs needing attention — the badge count. */
  needsAttentionCount: number;
  /** Enabled jobs, total. */
  enabledCount: number;
  /**
   * True when there are enabled jobs and EVERY one of them is overdue. That is
   * not several coincidences: it means the external tick is not arriving, which
   * no per-job action will fix.
   */
  runtimeDark: boolean;
  /** Most recent run across all jobs, or null when nothing has ever run. */
  lastRunAnywhere: string | null;
  /** One sentence for a banner or a tooltip; null when there is nothing to say. */
  headline: string | null;
}

export function judgeAmbientRuntime(
  jobs: AmbientJobHealthInput[],
  nowIso: string,
): AmbientRuntimeHealth {
  const enabled = jobs.filter((j) => j.enabled);
  const verdicts = enabled.map((j) => judgeAmbientJob(j, nowIso));
  const needsAttentionCount = verdicts.filter((v) => v.needsAttention).length;
  const stalled = verdicts.filter((v) => v.state === 'overdue' || v.state === 'never_run');

  const lastRunAnywhere =
    enabled
      .map((j) => j.lastRunAt)
      .filter((v): v is string => !!v)
      .sort()
      .at(-1) ?? null;

  const runtimeDark = enabled.length > 0 && stalled.length === enabled.length;

  let headline: string | null = null;
  if (runtimeDark) {
    const elapsed = minutesBetween(lastRunAnywhere, nowIso);
    headline = lastRunAnywhere
      ? `No ambient job has run in ${describeElapsed(elapsed ?? 0)}. The scheduler tick is not reaching this workspace — that is a deploy-environment setting, not something the app can restart.`
      : `None of the ${enabled.length} enabled job${enabled.length === 1 ? ' has' : 's have'} ever run. The scheduler tick has never reached this workspace — that is a deploy-environment setting, not something the app can restart.`;
  } else if (needsAttentionCount > 0) {
    headline = `${needsAttentionCount} of ${enabled.length} enabled job${enabled.length === 1 ? '' : 's'} need${needsAttentionCount === 1 ? 's' : ''} attention.`;
  }

  return {
    needsAttentionCount,
    enabledCount: enabled.length,
    runtimeDark,
    lastRunAnywhere,
    headline,
  };
}
